import React, { useEffect, useState } from 'react';
import { PaymentLogo, LABELS } from '../../components/common/PaymentIcon';
import api from '../../utils/api';

const TYPES = ['chime', 'cashapp', 'ltc', 'btc', 'paypal', 'other'];
const empty = { name: '', type: 'cashapp', tag: '', address: '', instructions: '', isActive: true, order: 0 };

export default function AdminPayments() {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [qrFile, setQrFile] = useState(null);
  const [qrPreview, setQrPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => api.get('/payments/admin').then(({ data }) => setMethods(data.methods || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(empty); setQrFile(null); setQrPreview(null); setError(''); setModal(true); };
  const openEdit = (m) => {
    setEditing(m._id);
    setForm({ name: m.name, type: m.type, tag: m.tag || '', address: m.address || '', instructions: m.instructions || '', isActive: m.isActive, order: m.order || 0 });
    setQrFile(null); setQrPreview(m.qrCode || null); setError(''); setModal(true);
  };

  const handleQr = (e) => {
    const f = e.target.files[0]; if (!f) return;
    setQrFile(f); setQrPreview(URL.createObjectURL(f));
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (qrFile) fd.append('qrCode', qrFile);
      if (editing) await api.put(`/payments/${editing}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      else await api.post('/payments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setModal(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Error saving'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment method?')) return;
    await api.delete(`/payments/${id}`); load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24 }}>PAYMENT METHODS</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>Manage deposit options shown to users — upload QR codes and tags</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Method</button>
      </div>

      {loading ? <div className="loading-spinner"><div className="spinner" /></div> : (
        <div className="grid-3">
          {methods.map(m => (
            <div key={m._id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Header with logo */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <PaymentLogo type={m.type} size={52} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 17 }}>{m.name || LABELS[m.type]}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>{m.type}</div>
                </div>
                <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 10, fontWeight: 700, background: m.isActive ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)', color: m.isActive ? '#10b981' : '#ef4444' }}>
                  {m.isActive ? 'Active' : 'Off'}
                </span>
              </div>

              {/* QR Code */}
              {m.qrCode && (
                <div style={{ background: '#fff', borderRadius: 10, padding: 10, display: 'flex', justifyContent: 'center' }}>
                  <img src={m.qrCode} alt="QR" style={{ width: 120, height: 120, objectFit: 'contain' }} />
                </div>
              )}

              {/* Details */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {m.tag && (
                  <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: '8px 12px' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.06em', marginBottom: 2 }}>TAG / USERNAME</div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#f5c400' }}>{m.tag}</div>
                  </div>
                )}
                {m.address && (
                  <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: '8px 12px' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.06em', marginBottom: 2 }}>ADDRESS</div>
                    <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-secondary)', wordBreak: 'break-all' }}>{m.address.slice(0, 36)}{m.address.length > 36 ? '...' : ''}</div>
                  </div>
                )}
                {m.instructions && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, padding: '4px 0' }}>ℹ️ {m.instructions.slice(0, 90)}{m.instructions.length > 90 ? '...' : ''}</div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
                <button className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={() => openEdit(m)}>✏ Edit</button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(m._id)}>🗑</button>
              </div>
            </div>
          ))}

          {methods.length === 0 && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 20 }}>
                {['cashapp', 'chime', 'btc', 'ltc', 'paypal'].map(t => <PaymentLogo key={t} type={t} size={40} style={{ opacity: 0.4 }} />)}
              </div>
              <p style={{ fontSize: 16 }}>No payment methods yet.</p>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 6 }}>Add Cash App, Chime, Bitcoin, Litecoin, or PayPal</p>
            </div>
          )}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" style={{ maxWidth: 580, maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setModal(false)}>✕</button>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 24 }}>
              {editing ? 'Edit Payment Method' : 'Add Payment Method'}
            </h2>

            {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px', color: '#ef4444', marginBottom: 16, fontSize: 13 }}>{error}</div>}

            <form onSubmit={handleSave}>
              {/* Type selector with logos */}
              <div className="form-group">
                <label>Payment Type *</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 4 }}>
                  {TYPES.map(t => (
                    <div key={t} onClick={() => setForm({ ...form, type: t, name: LABELS[t] })}
                      style={{ padding: '12px 8px', borderRadius: 10, border: `2px solid ${form.type === t ? '#f5c400' : 'var(--border)'}`, cursor: 'pointer', background: form.type === t ? 'rgba(245,196,0,0.08)' : 'var(--bg-secondary)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, transition: 'all 0.15s' }}>
                      <PaymentLogo type={t} size={36} />
                      <span style={{ fontSize: 12, fontWeight: 600, color: form.type === t ? '#f5c400' : 'var(--text-secondary)' }}>{LABELS[t]}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Display Name</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={`e.g. ${LABELS[form.type]}`} />
              </div>

              {/* QR Upload */}
              <div className="form-group">
                <label>QR Code Image</label>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', background: 'var(--bg-secondary)', border: '2px dashed var(--border)', borderRadius: 12, padding: 16 }}>
                  <div style={{ width: 88, height: 88, background: qrPreview ? '#fff' : 'var(--bg-card)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0, border: '1px solid var(--border)', padding: qrPreview ? 4 : 0 }}>
                    {qrPreview
                      ? <img src={qrPreview} alt="QR" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      : <div style={{ textAlign: 'center' }}><div style={{ fontSize: 28 }}>📷</div><div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>QR Code</div></div>
                    }
                  </div>
                  <div style={{ flex: 1 }}>
                    <input type="file" accept="image/*" onChange={handleQr} style={{ marginBottom: 6 }} />
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>Upload your payment QR code image<br />PNG or JPG, max 5MB</p>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Tag / Username <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(cashtag, chime tag, PayPal email)</span></label>
                <input value={form.tag} onChange={e => setForm({ ...form, tag: e.target.value })} placeholder={form.type === 'cashapp' ? '$yourcashtag' : form.type === 'chime' ? '@yourchimetag' : form.type === 'paypal' ? 'you@email.com' : 'Your tag or username'} />
              </div>

              <div className="form-group">
                <label>Wallet Address <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(for crypto only)</span></label>
                <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder={form.type === 'btc' ? '1BvBMSEYstWet...' : form.type === 'ltc' ? 'LTC1q...' : 'Wallet address'} style={{ fontFamily: 'monospace', fontSize: 13 }} />
              </div>

              <div className="form-group">
                <label>Instructions for Users</label>
                <textarea value={form.instructions} onChange={e => setForm({ ...form, instructions: e.target.value })} rows={2} placeholder="e.g. Send exact amount, include your username in the memo/note field..." />
              </div>

              <div style={{ display: 'flex', gap: 24, marginBottom: 20, alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: 'var(--text-secondary)' }}>
                  <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} style={{ width: 'auto' }} /> Active (visible to users)
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 600 }}>Order:</label>
                  <input type="number" value={form.order} onChange={e => setForm({ ...form, order: +e.target.value })} style={{ width: 70, padding: '6px 10px' }} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>{saving ? 'Saving...' : editing ? 'Update Method' : 'Add Method'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)} style={{ flex: 1 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
